import { useState, useEffect, useCallback, useRef } from 'react';
import { pickTokenFromStorage, clearAllAuthTokens } from '../utils/tokenStorage';

// 认证状态接口
export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: any | null;
  loading: boolean;
  error: string | null;
  permissions: string[];
}

// 认证Hook配置接口
export interface UseAuthConfig {
  // 注意：useAuth Hook 不使用服务发现获取自己的API地址
  // 请使用环境变量 REACT_APP_API_BASE_URL 配置自己的后端地址
  // 如需访问其他微服务，请直接使用 ServiceDiscovery 工具类
  enableServiceDiscovery?: boolean;
  serviceName?: string;
}

function readJwtRoleCodesFromToken(token: string): string[] {
  try {
    if (!token?.includes('.')) return [];
    const part = token.split('.')[1];
    if (!part) return [];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const payload = JSON.parse(atob(b64 + pad)) as { roles?: unknown };
    if (!Array.isArray(payload.roles)) return [];
    return payload.roles.filter((x): x is string => typeof x === 'string' && !!x.trim());
  } catch {
    return [];
  }
}

/**
 * 将 msLogin POST /api/auth/verify 的 payload 规范为与登录接口一致的 mslogin_user 结构，
 * 保证 roles、rolePermissions 写入本地后，侧边栏/调试里能看到全部角色（JWT 不含 rolePermissions）。
 */
/**
 * msOrg 生产：Nginx 将 `/api/mslogin-proxy/*` 反代到本机 msLogin，避免浏览器直连 `login.homaxs.com` 触发 CORS。
 * 与 msOrg `authWindowGlobals` / `netConfig.shouldMsLoginVerifyViaOrgOrigin` 一致；构建时若注入了
 * `REACT_APP_API_BASE_URL=https://login.homaxs.com/api`，仅靠 window 可能被覆盖顺序影响，此处再兜底。
 */
function resolveMsOrgHomaxsVerifyProxy(
  apiBaseUrl: string,
  authEndpoint: string
): { apiBaseUrl: string; authEndpoint: string } {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return { apiBaseUrl, authEndpoint };
  }
  const raw = (
    (typeof process !== 'undefined' && process.env?.REACT_APP_MSLOGIN_VERIFY_VIA_ORG_ORIGIN) ||
    ''
  )
    .trim()
    .toLowerCase();
  if (raw === 'false' || raw === '0' || raw === 'no') {
    return { apiBaseUrl, authEndpoint };
  }
  const h = window.location.hostname.toLowerCase();
  if (h === 'org.homaxs.com' || h === 'msorg.homaxs.com') {
    const origin = window.location.origin.replace(/\/$/, '');
    return {
      apiBaseUrl: `${origin}/api`,
      authEndpoint: '/mslogin-proxy/auth/verify',
    };
  }
  return { apiBaseUrl, authEndpoint };
}

function normalizeUserFromMsLoginVerifyPayload(
  payload: Record<string, any>,
  accessToken: string
) {
  const id = String(payload.adminId ?? payload.userId ?? payload.id ?? '');
  const account = String(payload.account ?? payload.username ?? '');
  const fromPayload = Array.isArray(payload.roles) ? payload.roles : [];
  const fromJwt = readJwtRoleCodesFromToken(accessToken);
  const roles = [...new Set([...fromPayload, ...fromJwt])];
  return {
    id,
    account,
    name: account,
    roles,
    roleDetails: Array.isArray(payload.roleDetails) ? payload.roleDetails : [],
    rolePermissions: Array.isArray(payload.rolePermissions)
      ? payload.rolePermissions
      : [],
    permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
    isSuperAdmin: !!(payload.isSuperAdmin ?? payload.is_super_admin),
  };
}

/**
 * 首帧同步从 localStorage 恢复登录态，避免每个 AuthGuard 在 useEffect 前长期 loading: true
 * （msOrg 等应用在路由级多次挂载 AuthGuard，与 App 顶层各自一份 useAuth 状态）。
 */
function readInitialAuthFromStorage(): AuthState {
  const base: AuthState = {
    isAuthenticated: false,
    token: null,
    user: null,
    loading: true,
    error: null,
    permissions: [],
  };
  try {
    const token = pickTokenFromStorage();
    if (!token) {
      return { ...base, loading: false };
    }
    const cachedUser = globalThis.localStorage?.getItem('mslogin_user');
    if (!cachedUser) {
      return { ...base, token, loading: true };
    }
    try {
      const userData = JSON.parse(cachedUser) as Record<string, unknown>;
      const permissions = (Array.isArray(userData.permissions)
        ? userData.permissions
        : []) as string[];
      return {
        isAuthenticated: true,
        token,
        user: userData,
        loading: false,
        error: null,
        permissions,
      };
    } catch {
      return { ...base, token, loading: true };
    }
  } catch {
    return base;
  }
}

// 认证Hook
export const useAuth = (config?: UseAuthConfig) => {
  // 完全禁用调试信息输出
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[useAuth] Hook 开始初始化');
  // }

  const [authState, setAuthState] = useState<AuthState>(readInitialAuthFromStorage);

  // 防重复调用标志
  const [isChecking, setIsChecking] = useState(false);

  // 防抖机制，避免频繁认证检查
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(0);

  // 完全禁用调试信息输出
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[useAuth] 初始状态:', authState);
  // }

  // 检查认证状态
  const checkAuth = useCallback(async (silent: boolean = false) => {
    // 防止重复调用
    if (isChecking) {
      // 完全禁用调试信息输出
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('[useAuth] 认证检查正在进行中，跳过重复调用');
      // }
      return;
    }

    // 防抖机制：如果最近已经检查过，则跳过
    const now = Date.now();
    if (now - lastCheckRef.current < 2000) { // 增加到2秒内不重复检查
      // 完全禁用调试信息输出
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('[useAuth] 防抖机制：跳过频繁的认证检查');
      // }
      return;
    }

    // 完全禁用调试信息输出
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('[useAuth] checkAuth 函数被调用');
    // }
    setIsChecking(true);
    // 静默模式下不设置loading，避免页面闪烁
    if (!silent) {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
    }
    lastCheckRef.current = now;

    try {
      const token = pickTokenFromStorage();

      // 完全禁用调试信息输出
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('[useAuth] Token检查结果:', {
      //     hasToken: !!token,
      //     tokenLength: token ? token.length : 0,
      //     tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
      //     localStorage: {
      //       token: localStorage.getItem('token') ? 'exists' : 'null',
      //       mslogin_token: localStorage.getItem('mslogin_token') ? 'exists' : 'null'
      //     },
      //     sessionStorage: {
      //       token: sessionStorage.getItem('token') ? 'exists' : 'null',
      //       mslogin_token: sessionStorage.getItem('mslogin_token') ? 'exists' : 'null'
      //     }
      //   });
      // }

      if (!token) {
        // 完全禁用调试信息输出
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('[useAuth] 没有找到token，认证失败');
        // }
        setAuthState({
          isAuthenticated: false,
          token: null,
          user: null,
          loading: false,
          error: null,
          permissions: [],
        });
        return;
      }

      // 验证token的有效性
      try {
        // 获取当前微服务的API基础URL
        let apiBaseUrl: string;

        // 混合模式：useAuth 只使用环境变量配置自己的API地址
        // 服务发现应该用于获取其他微服务的地址，而不是自己的地址
        if (config?.enableServiceDiscovery && config?.serviceName) {
          console.warn('[useAuth] 警告：服务发现不应用于获取当前微服务自己的API地址');
          console.warn('[useAuth] 建议：请设置环境变量 REACT_APP_API_BASE_URL 或使用 enableServiceDiscovery: false');
        }

        // 始终使用环境变量配置自己的API地址
        apiBaseUrl = (typeof window !== 'undefined' && (window as any).REACT_APP_API_BASE_URL) ||
          process.env.REACT_APP_API_BASE_URL ||
          (typeof window !== 'undefined' && (window as any).REACT_APP_API_URL) ||
          process.env.REACT_APP_API_URL || '';

        // 启用调试信息输出
        if (process.env.NODE_ENV === 'development') {
          console.log('[useAuth] 环境变量检查:', {
            window_REACT_APP_API_BASE_URL: (typeof window !== 'undefined' && (window as any).REACT_APP_API_BASE_URL),
            process_env_REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
            window_REACT_APP_API_URL: (typeof window !== 'undefined' && (window as any).REACT_APP_API_URL),
            process_env_REACT_APP_API_URL: process.env.REACT_APP_API_URL,
            finalApiBaseUrl: apiBaseUrl
          });
        }

        // 如果环境变量未配置，根据当前域名判断API地址
        if (!apiBaseUrl) {
          if (typeof window !== 'undefined' && window.location) {
            const hostname = window.location.hostname;
            
            // 生产环境：根据域名判断使用统一的后端API地址
            if (hostname.includes('homaxs.com') || hostname.includes('idtree.cn')) {
              // 生产环境统一使用 www.homaxs.com 作为后端API地址
              apiBaseUrl = 'https://www.homaxs.com';
              console.log('[useAuth] 生产环境，使用统一后端API地址:', apiBaseUrl);
            } else {
              // 开发环境或本地环境，使用当前域名
              apiBaseUrl = `${window.location.origin}`;
              console.log('[useAuth] 环境变量未配置，使用当前域名:', apiBaseUrl);
            }
          } else {
            // 开发环境 fallback
            console.warn('[useAuth] 环境变量未配置，使用开发环境默认配置');
            apiBaseUrl = 'http://localhost:50090';
          }
        }

        // 检查是否有重复的 /api
        if (apiBaseUrl.endsWith('/api/api')) {
          console.error('[useAuth] 检测到重复的 /api 路径，自动修复:', apiBaseUrl);
          apiBaseUrl = apiBaseUrl.replace('/api/api', '/api');
        }

        // 根据服务类型使用不同的认证端点
        // 默认使用 /api/auth/verify，因为后端路由通常挂载在 /api 前缀下
        let authEndpoint = (typeof window !== 'undefined' && (window as any).REACT_APP_AUTH_ENDPOINT) ||
          process.env.REACT_APP_AUTH_ENDPOINT ||
          '/api/auth/verify';

        // 使用配置的API基础URL，不再强制重定向到50091
        let finalApiBaseUrl = apiBaseUrl;
        const proxied = resolveMsOrgHomaxsVerifyProxy(finalApiBaseUrl, authEndpoint);
        finalApiBaseUrl = proxied.apiBaseUrl;
        authEndpoint = proxied.authEndpoint;

        // 启用调试信息输出
        if (process.env.NODE_ENV === 'development') {
          console.log('[useAuth] 使用API配置:', { apiBaseUrl, finalApiBaseUrl, authEndpoint });
          console.log('[useAuth] 发送认证请求到:', `${finalApiBaseUrl}${authEndpoint}`);
          console.log('[useAuth] 认证请求token:', token ? `${token.substring(0, 20)}...` : 'null');
        }

        const response = await fetch(`${finalApiBaseUrl}${authEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        // 完全禁用调试信息输出
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('[useAuth] 认证响应状态:', response.status);
        // }
        const result = await response.json();
        // 启用调试信息输出
        if (process.env.NODE_ENV === 'development') {
          console.log('[useAuth] 认证响应结果:', result);
          console.log('[useAuth] 响应数据结构:', {
            success: result.success,
            data: result.data,
            dataValid: result.data?.valid,
            dataAdmin: result.data?.admin,
            dataAdminKeys: result.data?.admin ? Object.keys(result.data.admin) : 'no admin data'
          });
        }

        if (result.success && result.data.valid) {
          // 启用调试信息输出
          if (process.env.NODE_ENV === 'development') {
            console.log('[useAuth] 认证成功，设置认证状态');
            console.log('[useAuth] 用户信息:', result.data.payload || result.data.admin);
          }

          // 兼容两种数据格式：payload 或 admin
          const userData = result.data.payload || result.data.admin;
          if (!userData) {
            throw new Error('认证响应中缺少用户数据');
          }
          const normalized = normalizeUserFromMsLoginVerifyPayload(userData, token);
          try {
            localStorage.setItem('mslogin_user', JSON.stringify(normalized));
            localStorage.setItem('token', token);
            localStorage.setItem('mslogin_token', token);
            sessionStorage.setItem('mslogin_token', token);
          } catch {
            /* ignore */
          }
          setAuthState({
            isAuthenticated: true,
            token,
            user: normalized,
            loading: false,
            error: null,
            permissions: (normalized.permissions as string[]) || [],
          });
          // 完全禁用调试信息输出
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('[useAuth] 认证状态已设置:', {
          //     isAuthenticated: true,
          //     hasToken: !!token,
          //     hasUser: !!result.data.admin
          //   });
          // }
        } else {
          // Token 无效：须同时清除 token 与 mslogin_token，否则仅清 token 会残留旧 mslogin_token，
          // 与后续「优先 mslogin」的 API 组合后永远发过期 JWT。
          clearAllAuthTokens();
          try {
            localStorage.removeItem('mslogin_user');
          } catch {
            /* ignore */
          }
          setAuthState({
            isAuthenticated: false,
            token: null,
            user: null,
            loading: false,
            error: 'Token已过期或无效',
            permissions: [],
          });
        }
      } catch (verifyError) {
        // 验票请求异常：不再假登录；清空令牌，避免界面显示已登录但 API 一律 401
        clearAllAuthTokens();
        try {
          localStorage.removeItem('mslogin_user');
        } catch {
          /* ignore */
        }
        setAuthState({
          isAuthenticated: false,
          token: null,
          user: null,
          loading: false,
          error: verifyError instanceof Error ? verifyError.message : '认证验证失败',
          permissions: [],
        });
      }
    } catch (error) {
        // 静默模式下，验证失败时不更新状态，保持当前状态
        if (!silent) {
          setAuthState({
            isAuthenticated: false,
            token: null,
            user: null,
            loading: false,
            error: error instanceof Error ? error.message : '认证检查失败',
            permissions: [],
          });
        }
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  // 登录
  const login = useCallback(async (token: string) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('mslogin_token', token);
      sessionStorage.setItem('mslogin_token', token);
      // 直接调用 checkAuth 逻辑，避免循环依赖
      if (!isChecking) {
        setIsChecking(true);
        setAuthState(prev => ({ ...prev, loading: true, error: null }));

        try {
          // 获取API基础URL
          let apiBaseUrl = (typeof window !== 'undefined' && (window as any).REACT_APP_API_BASE_URL) ||
            process.env.REACT_APP_API_BASE_URL ||
            (typeof window !== 'undefined' && (window as any).REACT_APP_API_URL) ||
            process.env.REACT_APP_API_URL || 'http://localhost:50090/api';

          // 默认使用 /api/auth/verify，因为后端路由通常挂载在 /api 前缀下
          let authEndpoint = (typeof window !== 'undefined' && (window as any).REACT_APP_AUTH_ENDPOINT) ||
            process.env.REACT_APP_AUTH_ENDPOINT ||
            '/api/auth/verify';

          const proxiedLogin = resolveMsOrgHomaxsVerifyProxy(apiBaseUrl, authEndpoint);
          apiBaseUrl = proxiedLogin.apiBaseUrl;
          authEndpoint = proxiedLogin.authEndpoint;

          const response = await fetch(`${apiBaseUrl}${authEndpoint}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setAuthState({
              isAuthenticated: true,
              token: token,
              loading: false,
              error: null,
              user: userData.user || userData,
              permissions: (userData.user || userData)?.permissions || [],
            });
          } else {
            throw new Error('认证失败');
          }
        } catch (error) {
          setAuthState(prev => ({
            ...prev,
            isAuthenticated: false,
            loading: false,
            error: error instanceof Error ? error.message : '认证检查失败',
            permissions: [],
          }));
        } finally {
          setIsChecking(false);
        }
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : '登录失败',
        permissions: [],
      }));
    }
  }, [isChecking]);

  // 登出
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('mslogin_token');
    sessionStorage.removeItem('mslogin_token');
    localStorage.removeItem('mslogin_user');
    localStorage.removeItem('mslogin_refresh_token');
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null,
      loading: false,
      error: null,
      permissions: [],
    });
  }, []);

  useEffect(() => {
    // 先检查本地缓存，如果有用户信息且token存在，先使用缓存，避免立即显示loading
    const token = localStorage.getItem('token') ||
      sessionStorage.getItem('token') ||
      localStorage.getItem('mslogin_token') ||
      sessionStorage.getItem('mslogin_token');
    
    const cachedUser = localStorage.getItem('mslogin_user');
    
    // 如果有token和缓存的用户信息，先使用缓存，避免页面闪烁
    if (token && cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        const permissions = userData.permissions || [];
        
        // 先设置缓存状态，不显示loading
        setAuthState({
          isAuthenticated: true,
          token: token,
          user: userData,
          loading: false, // 不显示loading，使用缓存
          error: null,
          permissions: permissions,
        });
        
        // 然后在后台静默验证token（不显示loading）
        // 防抖执行认证检查
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
          // 静默验证，不设置loading状态
          checkAuth(true).catch(() => {
            // 验证失败时保持缓存状态，不强制跳转
          });
        }, 300); // 短延迟后台验票，避免与首屏数据请求抢连接

        return () => {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }
        };
      } catch (error) {
        // 缓存解析失败，继续正常流程
        console.warn('[useAuth] 缓存用户信息解析失败，使用正常验证流程');
      }
    }
    
    // 没有缓存时，执行正常认证检查
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      checkAuth();
    }, 0); // 无本地用户缓存时尽快验票，避免首屏多等半秒

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []); // 移除 checkAuth 依赖，避免循环依赖

  // 权限检查函数
  const hasPermission = useCallback((permission: string) => {
    if (authState.permissions.includes('*')) return true;
    return authState.permissions.includes(permission);
  }, [authState.permissions]);

  const hasAnyPermission = useCallback((permissions: string[]) => {
    if (authState.permissions.includes('*')) return true;
    return permissions.some(permission => authState.permissions.includes(permission));
  }, [authState.permissions]);

  const hasAllPermissions = useCallback((permissions: string[]) => {
    if (authState.permissions.includes('*')) return true;
    return permissions.every(permission => authState.permissions.includes(permission));
  }, [authState.permissions]);

  return {
    ...authState,
    checkAuth,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
